import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layout, Typography, Select, Tabs, Input, Button, Form, Row, Col, DatePicker,
  Table, Upload, Space, Divider, Checkbox, Avatar, Card, Modal, Descriptions,
  Badge, Progress, Tag, Tooltip, App
} from 'antd';
import {
  UserOutlined, EnvironmentOutlined, UploadOutlined, LogoutOutlined,
  EditOutlined, EyeOutlined, FileOutlined, SendOutlined, SearchOutlined,
  CalendarOutlined, TeamOutlined, HomeOutlined, SafetyOutlined,
  CheckCircleOutlined, ClockCircleOutlined, StarOutlined, HeartOutlined,
  PhoneOutlined, MailOutlined, GlobalOutlined, FilterOutlined,
  PlusOutlined, DownloadOutlined, SyncOutlined, BellOutlined, MenuOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import moment from 'moment';

const { Content, Header } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Search } = Input;

// Enhanced theme configuration
const theme = {
  colors: {
    primary: '#3B82F6',
    secondary: '#6B7280',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
    neutral: '#6B7280',
    lightGray: '#F9FAFB',
    mediumGray: '#E5E7EB',
    darkGray: '#1F2937',
    white: '#FFFFFF',
    border: '#D1D5DB'
  },
  shadows: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px'
  }
};

// Ajout d'une fonction utilitaire pour détecter le mobile
const isMobile = () => window.innerWidth <= 768;

// Enhanced styles
const styles = {
  layout: {
    minHeight: '100vh',
    backgroundColor: theme.colors.lightGray,
    width: '100%',
    overflowX: 'hidden',
  },
  header: {
    backgroundColor: theme.colors.white,
    padding: isMobile() ? '0 8px' : '0 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: theme.shadows.sm,
    borderBottom: `1px solid ${theme.colors.border}`,
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    height: isMobile() ? '56px' : '72px',
    borderRadius: isMobile() ? '0 0 8px 8px' : '0',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile() ? '6px' : '12px',
  },
  logoIcon: {
    width: isMobile() ? '36px' : '60px',
    height: isMobile() ? '36px' : '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: isMobile() ? '14px' : '20px',
  },
  logoText: {
    margin: 0,
    color: theme.colors.darkGray,
    fontWeight: 700,
    fontSize: isMobile() ? '15px' : '20px',
    lineHeight: 1.2,
  },
  logoSubtitle: {
    color: theme.colors.neutral,
    fontSize: isMobile() ? '10px' : '13px',
    fontWeight: 500,
  },
  navItem: {
    color: theme.colors.neutral,
    fontWeight: 500,
    padding: isMobile() ? '6px 10px' : '8px 16px',
    borderRadius: theme.borderRadius.md,
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: isMobile() ? '12px' : '14px',
  },
  navItemActive: {
    backgroundColor: theme.colors.primary,
    color: 'white',
  },
  content: {
    padding: isMobile() ? '8px' : '32px',
    maxWidth: isMobile() ? '100%' : '1200px',
    margin: '0 auto',
    width: '100%',
  },
  pageHeader: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: isMobile() ? '12px' : '32px',
    marginBottom: isMobile() ? '8px' : '24px',
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.border}`,
  },
  pageTitle: {
    margin: 0,
    color: theme.colors.darkGray,
    fontWeight: 700,
    fontSize: isMobile() ? '20px' : '32px',
    lineHeight: 1.2,
  },
  pageDescription: {
    color: theme.colors.neutral,
    fontSize: isMobile() ? '12px' : '16px',
    margin: isMobile() ? '6px 0 12px 0' : '8px 0 24px 0',
    lineHeight: 1.5,
  },
  statsContainer: {
    display: 'flex',
    gap: isMobile() ? '8px' : '16px',
    marginTop: isMobile() ? '12px' : '24px',
    flexWrap: 'wrap',
    flexDirection: isMobile() ? 'column' : 'row',
  },
  statCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: isMobile() ? '10px' : '20px',
    textAlign: 'center',
    border: `1px solid ${theme.colors.border}`,
    minWidth: isMobile() ? '100px' : '140px',
    transition: 'all 0.2s ease',
    fontSize: isMobile() ? '12px' : '14px',
  },
  tabsContainer: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: isMobile() ? '8px' : '24px',
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.border}`,
  },
  sectionCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: isMobile() ? '8px' : '24px',
    marginBottom: isMobile() ? '8px' : '16px',
    border: `1px solid ${theme.colors.border}`,
  },
  providerCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
    transition: 'all 0.2s ease',
    overflow: 'hidden',
    marginBottom: isMobile() ? '8px' : '0',
  },
  searchFilterContainer: {
    display: 'flex',
    gap: isMobile() ? '6px' : '12px',
    marginBottom: isMobile() ? '12px' : '24px',
    flexWrap: 'wrap',
    flexDirection: isMobile() ? 'column' : 'row',
  },
  modernInput: {
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
    transition: 'all 0.2s ease',
    fontSize: isMobile() ? '12px' : '14px',
  },
  modernButton: {
    borderRadius: theme.borderRadius.md,
    fontWeight: 500,
    transition: 'all 0.2s ease',
    border: 'none',
    height: isMobile() ? '32px' : '40px',
    fontSize: isMobile() ? '12px' : '14px',
  },
  primaryButton: {
    backgroundColor: theme.colors.primary,
    color: 'white',
    fontSize: isMobile() ? '12px' : '14px',
    borderRadius: theme.borderRadius.md,
    height: isMobile() ? '32px' : '40px',
  },
  secondaryButton: {
    backgroundColor: theme.colors.white,
    color: theme.colors.neutral,
    border: `1px solid ${theme.colors.border}`,
    fontSize: isMobile() ? '12px' : '14px',
    borderRadius: theme.borderRadius.md,
    height: isMobile() ? '32px' : '40px',
  },
  supportRow: {
    display: 'flex',
    alignItems: 'center',
    padding: isMobile() ? '8px 10px' : '16px 20px',
    borderRadius: theme.borderRadius.md,
    marginBottom: isMobile() ? '2px' : '4px',
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.border}`,
    transition: 'all 0.2s ease',
    fontSize: isMobile() ? '12px' : '14px',
  },
  supportCol: {
    flex: 1,
    textAlign: 'center',
    fontSize: isMobile() ? '12px' : '14px',
  },
  serviceNameCol: {
    flex: 2,
    textAlign: 'left',
    fontWeight: 500,
    color: theme.colors.darkGray,
    fontSize: isMobile() ? '12px' : '14px',
  },
  activityTable: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    border: `1px solid ${theme.colors.border}`,
    fontSize: isMobile() ? '12px' : '14px',
  },
  modalContent: {
    borderRadius: theme.borderRadius.lg,
    padding: isMobile() ? '8px' : '24px',
  },
  uploadArea: {
    backgroundColor: theme.colors.lightGray,
    border: `2px dashed ${theme.colors.mediumGray}`,
    borderRadius: theme.borderRadius.md,
    padding: isMobile() ? '16px' : '32px',
    textAlign: 'center',
    transition: 'all 0.2s ease',
    fontSize: isMobile() ? '12px' : '14px',
  },
  emptyState: {
    textAlign: 'center',
    padding: isMobile() ? '24px 8px' : '48px 24px',
    backgroundColor: theme.colors.lightGray,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
    fontSize: isMobile() ? '12px' : '14px',
  },
  iconContainer: {
    width: isMobile() ? '36px' : '64px',
    height: isMobile() ? '36px' : '64px',
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.colors.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: isMobile() ? '18px' : '28px',
    margin: isMobile() ? '0 auto 8px' : '0 auto 16px',
  }
};

// Subtle animations
const animations = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  },
  slideUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  },
  scale: {
    initial: { opacity: 0, scale: 0.98 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.98 }
  }
};

// Custom hook for user data management
const useUserData = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { notification } = App.useApp();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        console.log('Fetching user data from /backend/get_profile.php');

        const response = await fetch('/backend/get_profile.php', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers));

        if (!response.ok) {
          if (response.status === 401) {
            console.log('User not authenticated, redirecting to login');
            navigate('/login');
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('API Response:', JSON.stringify(data, null, 2));

        if (data.userName || data.nhGuideNumber) {
          const location = [data.street, data.city].filter(Boolean).join(', ') || 'Location not set';

          const mappedUserData = {
            name: data.userName || 'User',
            location: location,
            nhNumber: data.nh_guide_number || data.nhGuideNumber || 'Not assigned',
            avatar: data.profile_picture || data.profilePicture || null,
            notifications: data.notifications || 0,
            lastLogin: new Date().toLocaleDateString(),
            role: data.role || 'Member',
          };

          console.log('Mapped user data:', mappedUserData);
          setUserData(mappedUserData);
        } else {
          console.error('API returned no valid user data or success: false', data);

          if (data.message && (data.message.toLowerCase().includes('not logged in') || data.message.toLowerCase().includes('authentication'))) {
            console.log('Authentication issue, redirecting to login');
            navigate('/login');
            return;
          }

          console.log('Using fallback user data due to API error');
          setUserData({
            name: 'Guest User',
            location: 'Location not available',
            nhNumber: 'Not assigned',
            avatar: null,
            notifications: 0,
            lastLogin: new Date().toLocaleDateString(),
            role: 'Member',
          });

          notification.warning({
            message: 'Data Load Warning',
            description: data.message || 'Some user information could not be loaded',
            placement: 'topRight',
            duration: 3,
            style: { zIndex: 9999 },
            onClose: () => navigate('/login'),
          });
        }
      } catch (err) {
        console.error('Error fetching user data:', err);

        if (err.name === 'TypeError' && err.message.includes('fetch')) {
          setError('Network error - please check your connection');
          notification.error({
            message: 'Network Error',
            description: 'Network error - please check your connection',
            placement: 'topRight',
            duration: 3,
            style: { zIndex: 9999 },
          });
        } else if (err.message.includes('401')) {
          console.log('Authentication error, redirecting to login');
          navigate('/login');
          return;
        } else {
          console.log('Using fallback user data due to error');
          setUserData({
            name: 'Guest User',
            location: 'Location not available',
            nhNumber: 'Not assigned',
            avatar: null,
            notifications: 0,
            lastLogin: new Date().toLocaleDateString(),
            role: 'Member',
          });

          notification.warning({
            message: 'Data Load Warning',
            description: 'Some user information could not be loaded',
            placement: 'topRight',
            duration: 3,
            style: { zIndex: 9999 },
            onClose: () => navigate('/login'),
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate, notification]);

  return { userData, loading, error };
};

// Modern Header avec menu hamburger mobile
const ModernHeader = React.memo(() => {
  const navigate = useNavigate();
  const { userData, loading, error } = useUserData();
  const { notification } = App.useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = useCallback(async () => {
    try {
      const response = await fetch('/backend/logout.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        notification.success({
          message: 'Logout Successful',
          description: 'You have been successfully logged out.',
          placement: 'topRight',
          style: { zIndex: 9999 },
        });

        navigate('/login');
      } else {
        notification.error({
          message: 'Logout Error',
          description: data.error || 'Error during logout',
          placement: 'topRight',
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      notification.error({
        message: 'Connection Error',
        description: 'An error occurred during logout',
        placement: 'topRight',
        style: { zIndex: 9999 },
      });
    }
  }, [navigate, notification]);

  return (
    <motion.div
      style={styles.header}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div style={styles.logoContainer}>
        <div style={styles.logoIcon}>
          <Avatar
            icon={<UserOutlined />}
            size={isMobile() ? 36 : 64}
            className="ant-avatar"
            src="components/nhguidelogo.jpg"
          />
        </div>
        <div>
          <Title level={4} style={styles.logoText}>NH Guide</Title>
          <Text style={styles.logoSubtitle}>Support Services</Text>
        </div>
      </div>
      {/* Desktop nav */}
      <Space size={isMobile() ? 0 : 24} style={{ display: isMobile() ? 'none' : 'flex' }}>
        {[
          { to: "/home", icon: <HomeOutlined />, label: "Home" },
          { to: "/profile", icon: <UserOutlined />, label: "Profile" },
          { to: "/ServiceProviders", icon: <TeamOutlined />, label: "Services", active: true },
          { to: "/DataPrivacy", icon: <SafetyOutlined />, label: "Privacy" }
        ].map((item) => (
          <motion.div
            key={item.to}
            whileHover={{ y: -2 }}
            transition={{ duration: 0.2 }}
          >
            <Link
              to={item.to}
              style={{
                ...styles.navItem,
                ...(item.active ? styles.navItemActive : {})
              }}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          </motion.div>
        ))}
      </Space>
      {/* Mobile hamburger */}
      <div style={{ display: isMobile() ? 'flex' : 'none', alignItems: 'center' }}>
        <Button
          type="text"
          icon={<MenuOutlined style={{ fontSize: 22 }} />}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{ ...styles.modernButton, background: theme.colors.lightGray, marginRight: 4 }}
        />
        <Modal
          open={mobileMenuOpen}
          onCancel={() => setMobileMenuOpen(false)}
          footer={null}
          closable={false}
          width="90vw"
          bodyStyle={{ padding: 0, background: theme.colors.white, borderRadius: 12 }}
          style={{ top: 8 }}
        >
          <div style={{ padding: 12 }}>
            {[
              { to: "/home", icon: <HomeOutlined />, label: "Home" },
              { to: "/profile", icon: <UserOutlined />, label: "Profile" },
              { to: "/ServiceProviders", icon: <TeamOutlined />, label: "Services", active: true },
              { to: "/DataPrivacy", icon: <SafetyOutlined />, label: "Privacy" }
            ].map((item) => (
              <Button
                key={item.to}
                type="text"
                block
                icon={item.icon}
                style={{
                  ...styles.navItem,
                  ...(item.active ? styles.navItemActive : {}),
                  justifyContent: 'flex-start',
                  marginBottom: 4,
                  fontSize: 15,
                }}
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.location.href = item.to;
                }}
              >
                {item.label}
              </Button>
            ))}
            <Divider style={{ margin: '8px 0' }} />
            <Button
              type="text"
              icon={<LogoutOutlined />}
              block
              style={{ color: theme.colors.error, fontWeight: 600, fontSize: 15 }}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </div>
        </Modal>
      </div>
      {/* User info & notif */}
      <Space align="center" size={isMobile() ? 6 : 16}>
        <Button type="text" icon={<BellOutlined />} size={isMobile() ? 'middle' : 'small'}>
          <Badge count={userData?.notifications || 3} size="small" />
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile() ? '4px' : '8px' }}>
          <Avatar
            icon={<UserOutlined />}
            src={userData?.avatar}
            size={isMobile() ? 28 : 36}
            style={{ backgroundColor: theme.colors.primary }}
          />
          <div>
            <Text strong style={{ fontSize: isMobile() ? '11px' : '13px', color: theme.colors.darkGray }}>
              {loading ? 'Loading...' : userData?.name || 'User'}
            </Text>
            <br />
            <Text style={{ fontSize: isMobile() ? '9px' : '11px', color: theme.colors.neutral }}>
              {loading ? '...' : userData?.role || 'Active Member'}
            </Text>
          </div>
        </div>
      </Space>
    </motion.div>
  );
});

// Enhanced Stats Card
const StatsCard = React.memo(({ icon, value, label, color = theme.colors.primary }) => (
  <motion.div
    style={{
      ...styles.statCard,
      borderLeft: `3px solid ${color}`,
    }}
    whileHover={{ y: -2, boxShadow: theme.shadows.md }}
    transition={{ duration: 0.2 }}
  >
    <div style={{ fontSize: '20px', color, marginBottom: '8px' }}>
      {icon}
    </div>
    <div style={{ fontSize: '20px', fontWeight: 'bold', color: theme.colors.darkGray, marginBottom: '4px' }}>
      {value}
    </div>
    <div style={{ fontSize: '11px', color: theme.colors.neutral, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </div>
  </motion.div>
));

// Enhanced Provider Card
const ProviderCard = React.memo(({ admin, onSendRequest, requestLoading }) => (
  <motion.div
    whileHover={{ y: -2, boxShadow: theme.shadows.md }}
    transition={{ duration: 0.2 }}
  >
    <Card
      style={styles.providerCard}
      hoverable
      actions={[
        <Button
          key="send-request"
          type="primary"
          icon={<SendOutlined />}
          onClick={() => onSendRequest(admin.email)}
          style={styles.primaryButton}
          size="small"
          loading={requestLoading[admin.email]}
        >
          {requestLoading[admin.email] ? 'Sending...' : 'Send Request'}
        </Button>,
      ]}
    >
      <Card.Meta
        avatar={
          <Avatar
            size={48}
            icon={<UserOutlined />}
            style={{ backgroundColor: theme.colors.primary }}
          />
        }
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontWeight: 600, color: theme.colors.darkGray, fontSize: '15px' }}>
              {admin.full_names || 'Name not available'}
            </span>
            <Tag color="success" size="small">
              {admin.status || 'Active'}
            </Tag>
          </div>
        }
        description={
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <MailOutlined style={{ color: theme.colors.info, fontSize: '12px' }} />
              <Text style={{ color: theme.colors.neutral, fontSize: '13px' }}>
                {admin.email || 'N/A'}
              </Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <TeamOutlined style={{ color: theme.colors.warning, fontSize: '12px' }} />
              <Text style={{ color: theme.colors.neutral, fontSize: '13px' }}>
                {admin.category || 'N/A'}
              </Text>
            </div>
          </Space>
        }
      />
    </Card>
  </motion.div>
));

// Enhanced Support Row
const SupportRow = React.memo(({ item, index, onCheckboxChange }) => (
  <motion.div
    style={styles.supportRow}
    whileHover={{ backgroundColor: theme.colors.lightGray }}
    transition={{ duration: 0.2 }}
  >
    <div style={styles.serviceNameCol}>
      <Text strong style={{ fontSize: '14px' }}>{item.name}</Text>
    </div>
    <div style={styles.supportCol}>
      <Checkbox
        checked={item.need}
        onChange={(e) => onCheckboxChange(index, "need", e.target.checked)}
      />
    </div>
    <div style={styles.supportCol}>
      <Checkbox
        checked={item.connected}
        onChange={(e) => onCheckboxChange(index, "connected", e.target.checked)}
      />
    </div>
  </motion.div>
));

const ServiceProviders = () => {
  const [filter, setFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('3');
  const [form] = Form.useForm();
  const [supportForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [documentForm] = Form.useForm();
  const [supportHistory, setSupportHistory] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState({});
  const { notification } = App.useApp();

  const services = [
    "Drop-in Services (Shower, Mail etc)",
    "Emergency Shelter",
    "Eviction Prevention Financial Aid",
    "Food Assistance (Hot Meals, Food Pantries etc)",
    "Foreclosure Prevention Financial Aid",
    "Healthcare/Medical Services",
    "Job Training",
    "Housing Search Assistance",
    "Legal Aid/Counseling",
    "Mental Health Services",
    "Rent and Deposit Assistance",
    "Transportation",
    "Substance Use Treatment Services",
  ];

  const [supportData, setSupportData] = useState(
    services.map((service) => ({
      name: service,
      need: false,
      connected: false,
    }))
  );

  // Calculate statistics
  const stats = useMemo(() => ({
    totalProviders: admins.length,
    activeConnections: supportHistory.length,
    completedActivities: activityData.length,
    documentsUploaded: documents.length,
  }), [admins.length, supportHistory.length, activityData.length, documents.length]);

  const fetchAdmins = useCallback(async (category = '') => {
    setLoading(true);
    try {
      const url = category
        ? `/backend/provider_get_admins_by_category.php?category=${encodeURIComponent(category)}`
        : '/backend/provider_get_admins_by_category.php';

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const text = await response.text();
      let data;

      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Non-JSON response');
      }

      if (response.ok) {
        setAdmins(data.admins || []);
      } else {
        setAdmins([]);
        notification.error({
          message: 'Loading Error',
          description: data.error || 'Unable to load service providers',
          placement: 'topRight',
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      setAdmins([]);
      notification.error({
        message: 'Connection Error',
        description: 'Unable to connect to the server',
        placement: 'topRight',
        style: { zIndex: 9999 },
      });
    } finally {
      setLoading(false);
    }
  }, [notification]);

  const fetchSupportHistory = useCallback(async () => {
    try {
      const response = await fetch('/backend/get_support_history.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        const parsedData = data.map(record => ({
          ...record,
          admin_connections: record.admin_connections ? JSON.parse(record.admin_connections) : [],
        }));

        setSupportHistory(parsedData);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    }
  }, []);

  const fetchServiceActivities = useCallback(async () => {
    try {
      const response = await fetch('/backend/get_service_activities.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        setActivityData(data.activities || []);
      }
    } catch (error) {
      console.error('Error loading activities:', error);
    }
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await fetch('/backend/get_documents.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        setDocuments(data);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch('/backend/get_categories.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchSupportHistory();
    fetchServiceActivities();
    fetchDocuments();
    fetchAdmins();
    fetchCategories();
  }, [fetchSupportHistory, fetchServiceActivities, fetchDocuments, fetchAdmins, fetchCategories]);

  const handleFilterChange = useCallback((value) => {
    setFilter(value);
    fetchAdmins(value);
  }, [fetchAdmins]);

  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const handleSendRequest = useCallback(async (adminEmail) => {
    if (requestLoading[adminEmail]) {
      console.log(`Request already in progress for ${adminEmail}`);
      return;
    }

    setRequestLoading((prev) => ({ ...prev, [adminEmail]: true }));
    const notificationKey = `request_${adminEmail}_${Date.now()}`;

    try {
      console.log(`Sending request to /backend/add_connection.php for ${adminEmail}`);
      notification.open({
        key: notificationKey,
        message: 'Envoi de la Requête',
        description: 'Connexion avec le fournisseur de services...',
        placement: 'topRight',
        icon: <SyncOutlined spin style={{ color: theme.colors.primary }} />,
        duration: 0,
        style: { zIndex: 9999 },
      });

      const response = await fetch('/backend/add_connection.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ adminEmail }),
      });

      console.log(`Response status: ${response.status}`);
      console.log(`Response ok: ${response.ok}`);

      const text = await response.text();
      console.log(`Raw response text: ${text}`);

      let data;
      try {
        data = JSON.parse(text);
        console.log('Parsed API Response:', JSON.stringify(data, null, 2));
      } catch (e) {
        console.error('Non-JSON response:', text);
        throw new Error('Invalid response format from server');
      }

      // Check for success based on response.ok and message content
      if (response.ok && data.message && data.message.toLowerCase().includes('successfully')) {
        console.log(`Connection successful for ${adminEmail}`);
        notification.success({
          key: notificationKey,
          message: 'Connection Established',
          description: `You have successfully connected to ${adminEmail}.`,
          placement: 'topRight',
          duration: 5,
          style: {
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.colors.success}`,
            backgroundColor: theme.colors.lightGray,
            zIndex: 9999,
          },
        });
        await fetchSupportHistory();
      } else {
        console.warn(`Connection failed for ${adminEmail}. Response:`, data);
        notification.error({
          key: notificationKey,
          message: 'Connection Failed',
          description: data.error || data.message || 'Unable to establish a connection with the service provider..',
          placement: 'topRight',
          duration: 5,
          style: {
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.colors.error}`,
            backgroundColor: theme.colors.lightGray,
            zIndex: 9999,
          },
        });
      }
    } catch (error) {
      console.error('Error in handleSendRequest:', error.message, error.stack);
      notification.error({
        key: notificationKey,
        message: 'Connection Error',
        description: error.message || 'An unexpected error occurred while connecting. Please try again later..',
        placement: 'topRight',
        duration: 5,
        style: {
          borderRadius: theme.borderRadius.md,
          border: `1px solid ${theme.colors.error}`,
          backgroundColor: theme.colors.lightGray,
          zIndex: 9999,
        },
      });
    } finally {
      setRequestLoading((prev) => ({ ...prev, [adminEmail]: false }));
      console.log(`Request completed for ${adminEmail}`);
    }
  }, [fetchSupportHistory, requestLoading, notification]);

  const handleCheckboxChange = useCallback((index, key, value) => {
    const updatedData = [...supportData];

    if (key === "need" && value) {
      updatedData[index].connected = false;
    } else if (key === "connected" && value) {
      updatedData[index].need = false;
    }

    updatedData[index][key] = value;
    setSupportData(updatedData);
  }, [supportData]);

  const onFinish = useCallback(async (values) => {
    try {
      const payload = {
        date: values.date ? values.date.format('YYYY-MM-DD') : null,
        location: values.location,
        serviceProvider: values.serviceProvider,
        primaryStaff: values.primaryStaff,
        secondaryStaff: values.secondaryStaff || null,
        staffName: values.staffName || null,
        activityType: values.activityType,
        serviceDelivery: values.serviceDelivery,
        notes: values.notes || null,
      };

      const response = await fetch('/backend/save_service_activity.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        await fetchServiceActivities();
        notification.success({
          message: 'Activity Saved',
          description: 'Service activity saved successfully!',
          placement: 'topRight',
          style: { zIndex: 9999 },
        });

        form.resetFields();
      } else {
        notification.error({
          message: 'Save Failed',
          description: result.error || 'Error saving activity.',
          placement: 'topRight',
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      notification.error({
        message: 'Connection Error',
        description: 'An error occurred while saving the activity.',
        placement: 'topRight',
        style: { zIndex: 9999 },
      });
    }
  }, [form, fetchServiceActivities, notification]);

  const onSupportSave = useCallback(async () => {
    try {
      const values = await supportForm.validateFields();
      const needs = supportData
        .filter(item => item.need)
        .map(item => item.name);

      const connections = supportData
        .filter(item => item.connected)
        .map(item => item.name);

      const payload = {
        supportNumber: values.supportNumber,
        recordType: values.recordType,
        needs: needs,
        connections: connections,
        status: values.status,
      };

      const response = await fetch('/backend/save_support.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        await fetchSupportHistory();
        notification.success({
          message: 'Information Saved',
          description: 'Support information saved successfully!',
          placement: 'topRight',
          style: { zIndex: 9999 },
        });
      } else {
        notification.error({
          message: 'Save Failed',
          description: result.error || 'Error saving support information.',
          placement: 'topRight',
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      notification.error({
        message: 'Validation Error',
        description: 'Please fill in all required fields.',
        placement: 'topRight',
        style: { zIndex: 9999 },
      });
    }
  }, [supportForm, supportData, fetchSupportHistory, notification]);

  const handleViewRecord = useCallback((record) => {
    setSelectedRecord(record);
    setIsViewModalVisible(true);
  }, []);

  const handleEditRecord = useCallback((record) => {
    setSelectedRecord(record);
    editForm.setFieldsValue({
      supportNumber: record.support_number,
      recordType: record.record_type,
      status: record.status,
    });

    const updatedSupportData = services.map((service) => ({
      name: service,
      need: record.needs.includes(service),
      connected: record.connections.includes(service),
    }));

    setSupportData(updatedSupportData);
    setIsEditModalVisible(true);
  }, [editForm, services]);

  const uploadProps = {
    name: 'file',
    multiple: false,
    beforeUpload: () => false,
    onChange(info) {
      if (info.fileList.length > 0) {
        documentForm.setFieldsValue({ upload: info.fileList });
      } else {
        documentForm.setFieldsValue({ upload: null });
      }
    },
  };

  const handleDocumentUpload = useCallback(async () => {
    try {
      await documentForm.validateFields();
      const values = documentForm.getFieldsValue();
      const file = values.upload[0].originFileObj;
      const description = values.description;
      const formData = new FormData();

      formData.append('file', file);
      formData.append('description', description);

      const response = await fetch('/backend/upload_document.php', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        await fetchDocuments();
        notification.success({
          message: 'Document Uploaded',
          description: 'Document saved successfully!',
          placement: 'topRight',
          style: { zIndex: 9999 },
        });

        documentForm.resetFields();
      } else {
        notification.error({
          message: 'Upload Failed',
          description: result.error || 'Error uploading document.',
          placement: 'topRight',
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      notification.error({
        message: 'Upload Error',
        description: 'An error occurred while uploading the document.',
        placement: 'topRight',
        style: { zIndex: 9999 },
      });
    }
  }, [documentForm, fetchDocuments, notification]);

  const filteredAdmins = useMemo(() => {
    let filtered = filter
      ? admins.filter((admin) =>
          admin.category &&
          admin.category.toLowerCase().replace(/\s+/g, '-') === filter.toLowerCase()
        )
      : admins;

    return searchTerm
      ? filtered.filter((admin) =>
          (admin.full_names && admin.full_names.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (admin.email && admin.email.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      : filtered;
  }, [admins, filter, searchTerm]);

  const activityColumns = [
    {
      title: 'Date',
      dataIndex: 'activity_date',
      key: 'activity_date',
      render: (date) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <CalendarOutlined style={{ color: theme.colors.primary, fontSize: '14px' }} />
          <Text style={{ fontSize: '13px' }}>{date}</Text>
        </div>
      )
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (location) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <EnvironmentOutlined style={{ color: theme.colors.success, fontSize: '14px' }} />
          <Text style={{ fontSize: '13px' }}>{location}</Text>
        </div>
      )
    },
    { title: 'Service Provider', dataIndex: 'service_provider', key: 'service_provider' },
    { title: 'Primary Staff', dataIndex: 'primary_staff', key: 'primary_staff' },
    { title: 'Activity Type', dataIndex: 'activity_type', key: 'activity_type' },
    { title: 'Service Delivery', dataIndex: 'service_delivery', key: 'service_delivery' },
    {
      title: 'Documents',
      key: 'documents',
      render: () => (
        documents.length > 0 ? (
          <Space>
            {documents.map((doc) => (
              <Tooltip key={doc.id} title="Download Document">
                <a
                  href={`/backend/${doc.file_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: theme.colors.info, fontSize: '13px' }}
                >
                  <FileOutlined /> {doc.description}
                </a>
              </Tooltip>
            ))}
          </Space>
        ) : (
          <Text type="secondary" style={{ fontSize: '13px' }}>No documents</Text>
        )
      ),
    },
  ];

  const renderServiceProviders = () => (
    <motion.div {...animations.slideUp} transition={{ duration: 0.4 }}>
      <div style={styles.sectionCard}>
        <div style={{ marginBottom: '24px' }}>
          <Title level={3} style={{ margin: 0, color: theme.colors.darkGray, fontWeight: 600 }}>
            Service Provider Directory
          </Title>
          <Paragraph style={{ color: theme.colors.neutral, fontSize: '14px', margin: '8px 0 0 0' }}>
            Find and connect with local service providers who can assist you with housing and support services.
          </Paragraph>
        </div>
        <div style={styles.searchFilterContainer}>
          <Search
            placeholder="Search by name or email..."
            onSearch={handleSearch}
            style={{ flex: 1, maxWidth: '350px' }}
            size="middle"
          />
          <Select
            placeholder="Filter by category"
            onChange={handleFilterChange}
            value={filter || undefined}
            style={{ minWidth: '180px' }}
            size="middle"
            allowClear
          >
            {categories.map((cat) => (
              <Option key={cat.id} value={cat.name.toLowerCase().replace(/\s+/g, '-')}>
                {cat.name}
              </Option>
            ))}
          </Select>
          
        </div>
        <AnimatePresence>
          {loading ? (
            <motion.div style={styles.emptyState} {...animations.scale}>
              <SyncOutlined spin style={{ fontSize: isMobile() ? '22px' : '32px', color: theme.colors.primary, marginBottom: '16px' }} />
              <Title level={4} style={{ color: theme.colors.neutral, fontSize: isMobile() ? '15px' : '18px' }}>
                Loading providers...
              </Title>
            </motion.div>
          ) : (
            <motion.div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile() ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: isMobile() ? '8px' : '16px',
              }}
              {...animations.fadeIn}
            >
              {filteredAdmins.length > 0 ? (
                filteredAdmins.map((admin, index) => (
                  <ProviderCard
                    key={index}
                    admin={admin}
                    onSendRequest={handleSendRequest}
                    requestLoading={requestLoading}
                  />
                ))
              ) : (
                <motion.div style={{ ...styles.emptyState, gridColumn: '1 / -1' }} {...animations.scale}>
                  <div style={styles.iconContainer}>
                    <Avatar
                      icon={<UserOutlined />}
                      size={isMobile() ? 36 : 64}
                      className="ant-avatar"
                      src="components/nhguidelogo.jpg"
                    />
                  </div>
                  <Title level={4} style={{ color: theme.colors.neutral, fontSize: isMobile() ? '15px' : '18px' }}>
                    No providers found
                  </Title>
                  <Text style={{ color: theme.colors.neutral, fontSize: isMobile() ? '12px' : '14px' }}>
                    Try adjusting your search or filter criteria.
                  </Text>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );

  const renderSupportActivity = () => (
    <motion.div {...animations.slideUp} transition={{ duration: 0.4 }}>
      <div style={styles.sectionCard}>
        <div style={{ marginBottom: '24px' }}>
          <Title level={3} style={{ margin: 0, color: theme.colors.darkGray, fontWeight: 600 }}>
            Service Activities
          </Title>
          <Paragraph style={{ color: theme.colors.neutral, fontSize: '14px', margin: '8px 0 0 0' }}>
            Record your interactions with service providers and track service delivery.
          </Paragraph>
        </div>
        <Form
          form={form}
          name="supportActivity"
          onFinish={onFinish}
          layout="vertical"
          style={{ marginBottom: '32px' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Date"
                name="date"
                rules={[{ required: true, message: 'Please select a date!' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select a date"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Location"
                name="location"
                rules={[{ required: true, message: 'Please enter a location!' }]}
              >
                <Input
                  prefix={<EnvironmentOutlined />}
                  placeholder="Street Name, City"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Service Provider Organization"
                name="serviceProvider"
                rules={[{ required: true, message: 'Please select an organization!' }]}
              >
                <Select placeholder="Select a provider">
                  {admins.map((admin) => (
                    <Option key={admin.email} value={admin.email}>
                      {admin.full_names || 'Nameless Provider'} ({admin.email || 'No email'})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Primary Staff Contact"
                name="primaryStaff"
                rules={[{ required: true, message: 'Please enter a contact!' }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Phone or Email"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Activity Type"
                name="activityType"
                rules={[{ required: true, message: 'Please select an activity type!' }]}
              >
                <Select placeholder="Select type">
                  <Option value="drop-in-services">Drop-in Services (Shower, Mail etc)</Option>
                  <Option value="emergency-shelter">Emergency Shelter Placement</Option>
                  <Option value="eviction-prevention">Eviction Prevention Financial Aid</Option>
                  <Option value="food-assistance">Food Assistance (Hot Meals, Food Pantries etc)</Option>
                  <Option value="healthcare">Healthcare/Medical Services</Option>
                  <Option value="housing-search">Housing Search Assistance</Option>
                  <Option value="job-training">Job Training</Option>
                  <Option value="legal-aid">Legal Aid/Counseling</Option>
                  <Option value="mental-health">Mental Health Services</Option>
                  <Option value="rent-help">Rent and Deposit Assistance</Option>
                  <Option value="transportation">Transportation</Option>
                  <Option value="substance-use">Substance Use Treatment Services</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Service Delivery Mode"
                name="serviceDelivery"
                rules={[{ required: true, message: 'Please select a service delivery mode!' }]}
              >
                <Select placeholder="Select mode">
                  <Option value="in-person">In Person</Option>
                  <Option value="phone">Phone</Option>
                  <Option value="online">Online</Option>
                  <Option value="mail">Mail</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea
              placeholder="Enter additional notes..."
              rows={3}
              style={{ borderRadius: theme.borderRadius.md }}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlusOutlined />}
              style={styles.primaryButton}
            >
              Save Activity
            </Button>
          </Form.Item>
        </Form>
        <div style={{ marginTop: '32px' }}>
          <Title level={4} style={{ margin: '0 0 16px 0', color: theme.colors.darkGray, fontWeight: 600 }}>
            Activity History
          </Title>
          <Table
            columns={activityColumns}
            dataSource={activityData}
            pagination={{ pageSize: 8, showSizeChanger: true, size: 'small' }}
            style={styles.activityTable}
            scroll={{ x: 1000 }}
            size="small"
          />
        </div>
        <div style={{ marginTop: '32px' }}>
          <Title level={4} style={{ margin: '0 0 16px 0', color: theme.colors.darkGray, fontWeight: 600 }}>
            Documents
          </Title>
          <Paragraph style={{ color: theme.colors.neutral, fontSize: '14px', margin: '8px 0 0 0' }}>
            Upload and manage documents related to your service activities.
          </Paragraph>
          <Form
            form={documentForm}
            layout="vertical"
            onFinish={handleDocumentUpload}
            style={{ marginTop: '16px' }}
          >
            <Form.Item
              label="Upload Document"
              name="upload"
              rules={[{ required: true, message: 'Please upload a document!' }]}
              valuePropName="fileList"
              getValueFromEvent={(e) => e.fileList}
            >
              <Upload {...uploadProps}>
                <Button
                  icon={<UploadOutlined />}
                  style={styles.secondaryButton}
                >
                  Choose File
                </Button>
              </Upload>
              <Text style={{ color: theme.colors.neutral, fontSize: '12px', margin: '8px', display: 'block' }}>
                Maximum file size: 5MB. Supported formats: PDF, Word, JPEG, PNG
              </Text>
            </Form.Item>
            <Form.Item
              label="Document Description"
              name="description"
              rules={[{ required: true, message: 'Please enter a document description!' }]}
            >
              <Input.TextArea
                placeholder="Enter document description"
                rows={3}
                style={{ borderRadius: theme.borderRadius.md }}
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<UploadOutlined />}
                style={styles.primaryButton}
              >
                Upload Document
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </motion.div>
  );

  const renderSupportServices = () => (
    <motion.div {...animations.slideUp} transition={{ duration: 0.4 }}>
      <div style={styles.sectionCard}>
        <div style={{ marginBottom: '24px' }}>
          <Title level={3} style={{ margin: 0, color: theme.colors.darkGray, fontWeight: 600 }}>
            Support Needs and Services
          </Title>
          <Paragraph style={{ color: theme.colors.neutral, fontSize: '14px', margin: '8px 0 0 0' }}>
            Manage the supports and services you need and are connected to. Update this section at any time.
          </Paragraph>
        </div>
        <div style={{
          display: 'flex',
          padding: '16px 20px',
          backgroundColor: theme.colors.primary,
          borderRadius: theme.borderRadius.md,
          marginBottom: '12px',
          color: 'white',
          fontWeight: 600,
          fontSize: '14px',
        }}>
          <div style={{ flex: 2 }}>Support Services</div>
          <div style={{ flex: 1, textAlign: 'center' }}>I Need</div>
          <div style={{ flex: 1, textAlign: 'center' }}>I'm Connected</div>
        </div>
        <AnimatePresence>
          {supportData.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.02 }}
            >
              <SupportRow
                item={item}
                index={index}
                onCheckboxChange={handleCheckboxChange}
              />
            </motion.div>
          ))}
        </AnimatePresence>
        <div style={{ marginTop: '32px' }}>
          <Title level={4} style={{ margin: '0 0 16px 0', color: theme.colors.darkGray, fontWeight: 600 }}>
            Support Record Information
          </Title>
          <Form form={supportForm} layout="vertical">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="supportNumber"
                  label="Support Record Number"
                  rules={[{ required: true, message: 'Please enter your support record number!' }]}
                >
                  <Input
                    placeholder="Enter your record number"
                    prefix={<FileOutlined />}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="recordType"
                  label="Record Type"
                  rules={[{ required: true, message: 'Please select a record type!' }]}
                >
                  <Select placeholder="Select record type">
                    <Option value="initial">Initial</Option>
                    <Option value="followup">Follow-up</Option>
                    <Option value="final">Final</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: 'Please select a status!' }]}
                >
                  <Select placeholder="Select status">
                    <Option value="pending">Pending</Option>
                    <Option value="active">Active</Option>
                    <Option value="completed">Completed</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button
                type="primary"
                onClick={onSupportSave}
                icon={<CheckCircleOutlined />}
                style={styles.primaryButton}
              >
                Save Support Information
              </Button>
            </Form.Item>
          </Form>
        </div>
        <div style={{ marginTop: '32px' }}>
          <Title level={4} style={{ margin: '0 0 16px 0', color: theme.colors.darkGray, fontWeight: 600 }}>
            Support History
          </Title>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px'
          }}>
            {supportHistory.length > 0 ? (
              supportHistory.map((record) => (
                <motion.div
                  key={record.key}
                  whileHover={{ y: -2, boxShadow: theme.shadows.md }}
                  transition={{ duration: 0.2 }}
                >
                  <Card
                    style={styles.providerCard}
                    hoverable
                    actions={[
                      <Tooltip title="View Details">
                        <EyeOutlined
                          key="view"
                          onClick={() => handleViewRecord(record)}
                          style={{ color: theme.colors.info }}
                        />
                      </Tooltip>,
                      <Tooltip title="Edit Record">
                        <EditOutlined
                          key="edit"
                          onClick={() => handleEditRecord(record)}
                          style={{ color: theme.colors.warning }}
                        />
                      </Tooltip>,
                    ]}
                  >
                    <Card.Meta
                      avatar={
                        <Avatar
                          icon={<FileOutlined />}
                          style={{ backgroundColor: theme.colors.success }}
                        />
                      }
                      title={`Record #${record.support_number || 'N/A'}`}
                      description={
                        <div>
                          <Tag color={record.status === 'pending' ? 'orange' : 'green'}>
                            {record.status || 'N/A'}
                          </Tag>
                          <div style={{ marginTop: '8px', color: theme.colors.neutral, fontSize: '13px' }}>
                            Type: {record.record_type || 'N/A'}
                          </div>
                        </div>
                      }
                    />
                  </Card>
                </motion.div>
              ))
            ) : (
              <motion.div style={{ ...styles.emptyState, gridColumn: '1 / -1' }} {...animations.scale}>
                <div style={styles.iconContainer}>
                  <FileOutlined />
                </div>
                <Title level={4} style={{ color: theme.colors.neutral }}>
                  No support records found
                </Title>
                <Text style={{ color: theme.colors.neutral }}>
                  Create your first support record to get started.
                </Text>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <App>
      <Layout style={styles.layout}>
        <ModernHeader />
        <Content style={styles.content}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div style={styles.pageHeader} {...animations.slideUp} transition={{ duration: 0.4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Title level={1} style={styles.pageTitle}>
                    Support Services
                  </Title>
                  <Paragraph style={styles.pageDescription}>
                    Manage your support needs, connect with service providers, and track your service activities.
                  </Paragraph>
                </div>
                <div style={styles.iconContainer}>
                  <Avatar
                    icon={<UserOutlined />}
                    size={64}
                    className="ant-avatar"
                    src="components/nhguidelogo.jpg"
                  />
                </div>
              </div>
              <div style={styles.statsContainer}>
                <StatsCard
                  icon={<TeamOutlined />}
                  value={stats.totalProviders}
                  label="Service Providers"
                  color={theme.colors.primary}
                />
                <StatsCard
                  icon={<HeartOutlined />}
                  value={stats.activeConnections}
                  label="Active Connections"
                  color={theme.colors.success}
                />
                <StatsCard
                  icon={<CheckCircleOutlined />}
                  value={stats.completedActivities}
                  label="Completed Activities"
                  color={theme.colors.info}
                />
                <StatsCard
                  icon={<FileOutlined />}
                  value={stats.documentsUploaded}
                  label="Documents"
                  color={theme.colors.warning}
                />
              </div>
            </motion.div>
            <motion.div style={styles.tabsContainer} {...animations.slideUp} transition={{ duration: 0.4, delay: 0.1 }}>
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                size="large"
                tabBarStyle={{
                  marginBottom: '24px',
                  borderBottom: `1px solid ${theme.colors.border}`,
                }}
                items={[
                  {
                    label: (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                        <HeartOutlined />
                        Support Needs
                      </span>
                    ),
                    key: '1',
                    children: renderSupportServices(),
                  },
                  {
                    label: (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                        <CheckCircleOutlined />
                        Service Activities
                      </span>
                    ),
                    key: '2',
                    children: renderSupportActivity(),
                  },
                  {
                    label: (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                        <TeamOutlined />
                        Service Providers
                      </span>
                    ),
                    key: '3',
                    children: renderServiceProviders(),
                  },
                ]}
              />
              <Modal
                title="Support Record Details"
                open={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                footer={[
                  <Button key="close" onClick={() => setIsViewModalVisible(false)}>
                    Close
                  </Button>,
                  <Button
                    key="edit"
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setIsViewModalVisible(false);
                      handleEditRecord(selectedRecord);
                    }}
                    style={styles.primaryButton}
                  >
                    Edit
                  </Button>,
                ]}
                style={styles.modalContent}
              >
                {selectedRecord && (
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Support Number">
                      {selectedRecord.support_number || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Record Type">
                      {selectedRecord.record_type || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={selectedRecord.status === 'pending' ? 'orange' : 'green'}>
                        {selectedRecord.status || 'N/A'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Needs">
                      {Array.isArray(selectedRecord.needs) && selectedRecord.needs.length > 0
                        ? selectedRecord.needs.join(', ')
                        : 'None'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Connections">
                      {Array.isArray(selectedRecord.connections) && selectedRecord.connections.length > 0
                        ? selectedRecord.connections.join(', ')
                        : 'None'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created At">
                      {selectedRecord.created_at}
                    </Descriptions.Item>
                  </Descriptions>
                )}
              </Modal>
              <Modal
                title="Edit Support Record"
                open={isEditModalVisible}
                onCancel={() => setIsEditModalVisible(false)}
                onOk={async () => {
                  try {
                    const values = await editForm.validateFields();
                    const needs = supportData
                      .filter(item => item.need)
                      .map(item => item.name);
                    const connections = supportData
                      .filter(item => item.connected)
                      .map(item => item.name);
                    const payload = {
                      id: selectedRecord.key,
                      supportNumber: values.supportNumber,
                      recordType: values.recordType,
                      needs: needs,
                      connections: connections,
                      status: values.status,
                    };

                    const response = await fetch('/backend/update_support.php', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify(payload),
                    });

                    const result = await response.json();

                    if (response.ok) {
                      await fetchSupportHistory();
                      setIsEditModalVisible(false);
                      notification.success({
                        message: 'Record Updated',
                        description: 'Information updated successfully!',
                        placement: 'topRight',
                        style: { zIndex: 9999 },
                      });
                    } else {
                      notification.error({
                        message: 'Update Failed',
                        description: result.error || 'Error updating information.',
                        placement: 'topRight',
                        style: { zIndex: 9999 },
                      });
                    }
                  } catch (error) {
                    notification.error({
                      message: 'Validation Error',
                      description: 'Please fill in all required fields.',
                      placement: 'topRight',
                      style: { zIndex: 9999 },
                    });
                  }
                }}
                okText="Save Changes"
                cancelText="Cancel"
                style={styles.modalContent}
              >
                <Form form={editForm} layout="vertical">
                  <Form.Item
                    name="supportNumber"
                    label="Support Record Number"
                    rules={[{ required: true, message: 'Please enter your support record number!' }]}
                  >
                    <Input placeholder="Enter your record number" />
                  </Form.Item>
                  <Form.Item
                    name="recordType"
                    label="Record Type"
                    rules={[{ required: true, message: 'Please select a record type!' }]}
                  >
                    <Select placeholder="Select record type">
                      <Option value="initial">Initial</Option>
                      <Option value="followup">Follow-up</Option>
                      <Option value="final">Final</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="status"
                    label="Status"
                    rules={[{ required: true, message: 'Please select a status!' }]}
                  >
                    <Select placeholder="Select status">
                      <Option value="pending">Pending</Option>
                      <Option value="active">Active</Option>
                      <Option value="completed">Completed</Option>
                    </Select>
                  </Form.Item>
                </Form>
              </Modal>
            </motion.div>
          </motion.div>
        </Content>
      </Layout>
    </App>
  );
};

export default ServiceProviders;